import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import propertyService from '../../services/propertyService';
import PropertyDetailsModal from '../common/PropertyDetailsModal';
import '../admin/AdminDashboard.css';

const AgentPropertyList = ({ agentId, onEditProperty, onPropertyChanged, onAddProperty }) => {
  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    approvalStatus: '',
    propertyType: '',
    listingType: ''
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showBoostComingSoon, setShowBoostComingSoon] = useState(false);

  // Video generation modal — Chunk 2: full configuration UI.
  // Two-column layout: left = photo picker (drag-to-reorder, 5-15 bounds),
  // right = format/motion/music/overlay selectors + live cost breakdown.
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoModalProperty, setVideoModalProperty] = useState(null);

  // Options payload from GET /api/videos/options — server-driven source of
  // truth for limits (minPhotos, maxPhotos), motion styles, music tracks,
  // base pricing, agent's points balance, and current month usage. Frontend
  // never hardcodes these values; they come from videoConfig.js via the API.
  const [videoOptions, setVideoOptions] = useState(null);
  const [videoOptionsLoading, setVideoOptionsLoading] = useState(false);
  const [videoOptionsError, setVideoOptionsError] = useState('');

  // Ordered list of selected photo URLs — order is the playback order in
  // the generated video, so we use an array (not a Set) and reorder via
  // drag-and-drop. Cap enforced by limits.maxPhotos from /options.
  const [selectedPhotos, setSelectedPhotos] = useState([]);

  // Format presets (9:16, 1:1, 16:9) — base video includes all three by
  // default per videoConfig.js pricing notes, but agents can untoggle any
  // they don't need. Stored as a Set of preset IDs.
  const [selectedFormats, setSelectedFormats] = useState(new Set());

  // Single-select: motion style + music track. Both default to the first
  // option returned by /options once it loads.
  const [motionStyle, setMotionStyle] = useState('');
  const [musicTrack, setMusicTrack] = useState('');

  // Overlay toggles — each maps to a priced add-on in /options.addOns.
  // Keyed by add-on id so the cost breakdown can iterate this map directly.
  const [overlayToggles, setOverlayToggles] = useState({});

  // Drag-to-reorder state for the selected-photos strip. dragIndex is the
  // index in selectedPhotos being dragged; dragOverIndex is the slot it's
  // hovering over so we can show a drop indicator.
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Submit state — POST /api/videos/jobs lifecycle.
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // After a successful submission, the modal swaps from configuration UI
  // to a "your video is generating" success state. `submittedJob` holds the
  // job document returned by the API (useful for showing the job ID, eta,
  // points-charged confirmation, etc.). `postSubmitBalance` is the refreshed
  // points balance after the deduction — fetched separately because the
  // POST response returns the job, not the updated agent.
  const [submitted, setSubmitted] = useState(false);
  const [submittedJob, setSubmittedJob] = useState(null);
  const [postSubmitBalance, setPostSubmitBalance] = useState(null);

  // Open / close helpers. openVideoModal kicks off the /options fetch so
  // by the time the user looks at the right column, motion styles, music
  // tracks, and pricing are populated.
  const openVideoModal = (property) => {
    setVideoModalProperty(property);
    setShowVideoModal(true);
    fetchVideoOptions();
  };
  const closeVideoModal = () => {
    setShowVideoModal(false);
    // Defer clearing the property + selection state until after the close
    // animation/state settles so the modal doesn't flash empty content on
    // its way out, and so reopening starts from a clean slate.
    setTimeout(() => {
      setVideoModalProperty(null);
      setSelectedPhotos([]);
      setSelectedFormats(new Set());
      setOverlayToggles({});
      setSubmitError('');
      setSubmitted(false);
      setSubmittedJob(null);
      setPostSubmitBalance(null);
      setDragIndex(null);
      setDragOverIndex(null);
    }, 200);
  };

  // Apply default selections from a /options payload. Split out from the
  // fetch path so reopening the modal re-seeds defaults even when the
  // payload is already cached — otherwise closeVideoModal's state reset
  // would leave the modal blank on second open.
  const seedDefaultsFromOptions = (data) => {
    if (!data) return;

    // Only 16:9 on by default (most common share target); agents opt-in
    // to the other formats. Falls back to the first available preset if
    // 16:9 isn't in the response so we never start with an empty set.
    const availablePresets = data?.selectors?.formatPresets || [];
    const defaultPreset = availablePresets.includes('16:9')
      ? '16:9'
      : availablePresets[0];
    setSelectedFormats(new Set(defaultPreset ? [defaultPreset] : []));

    const defaultMotion =
      data?.selectors?.defaultMotionStyle ||
      data?.selectors?.motionStyles?.[0]?.id ||
      '';
    setMotionStyle(defaultMotion);

    const firstMusic = data?.musicTracks?.[0]?._id || '';
    setMusicTrack(firstMusic);

    setOverlayToggles({
      showListingData: false,
      showAgentBranding: false,
    });
  };

  // Fetch /api/videos/options. Cached on the component — once loaded the
  // payload sticks around for the lifetime of this list mount, so opening
  // the modal a second time doesn't re-fetch unless there was a prior
  // error. Defaults are re-seeded via seedDefaultsFromOptions on every
  // open (see openVideoModal), independent of fetch caching.
  //
  // Response shape from the backend (see videoController.getVideoOptions):
  //   { musicTracks: [{ _id, title, artist, mood, durationSeconds, previewUrl }],
  //     voices:      [{ _id, displayName, description, gender, tone, accent }],
  //     pricing:     { version, schedule, breakdown },
  //     limits:      { minPhotos, maxPhotos, defaultPhotoCount, monthlyCap,
  //                    monthlyUsed, monthlyRemaining },
  //     agent:       { pointsBalance, totalVideosGenerated },
  //     selectors:   { formatPresets: ["9:16","1:1","16:9"],
  //                    motionStyles: [{ id, label, description? }],
  //                    defaultMotionStyle: "id" } }
  const fetchVideoOptions = async () => {
    if (videoOptions) {
      // Already cached — just re-seed defaults from existing payload.
      seedDefaultsFromOptions(videoOptions);
      return;
    }
    if (videoOptionsLoading) return;
    setVideoOptionsLoading(true);
    setVideoOptionsError('');
    try {
      const data = await propertyService.getVideoOptions();
      setVideoOptions(data);
      seedDefaultsFromOptions(data);
    } catch (err) {
      console.error('Failed to load video options:', err);
      setVideoOptionsError(
        err?.response?.data?.message || err?.message || 'Failed to load video options'
      );
    } finally {
      setVideoOptionsLoading(false);
    }
  };

  // Normalize property.photos to a flat array of URL strings — defensive
  // because different parts of the codebase store photos as either string[]
  // or [{ url }]. Anything we can't resolve to a string is filtered out.
  const getPropertyPhotoUrls = (property) => {
    if (!property) return [];
    const raw = property.photos || property.images || property.photoUrls || [];
    return raw
      .map(p => (typeof p === 'string' ? p : p?.url || p?.secure_url || ''))
      .filter(Boolean);
  };

  // Photo selection toggle — guards against exceeding limits.maxPhotos.
  // If the photo is already selected, removing it just drops it from the
  // ordered list; the rest keep their relative order.
  const togglePhotoSelection = (url) => {
    setSelectedPhotos(prev => {
      const idx = prev.indexOf(url);
      if (idx !== -1) {
        return prev.filter((_, i) => i !== idx);
      }
      const max = videoOptions?.limits?.maxPhotos ?? 15;
      if (prev.length >= max) return prev;
      return [...prev, url];
    });
  };

  // Drag handlers for reordering the selected strip. We swap by splice so
  // the dragged photo lands exactly where it was dropped, not just adjacent.
  const handlePhotoDragStart = (index) => setDragIndex(index);
  const handlePhotoDragOver = (e, index) => {
    e.preventDefault();
    if (index !== dragOverIndex) setDragOverIndex(index);
  };
  const handlePhotoDrop = (index) => {
    if (dragIndex === null || dragIndex === index) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }
    setSelectedPhotos(prev => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(index, 0, moved);
      return next;
    });
    setDragIndex(null);
    setDragOverIndex(null);
  };
  const handlePhotoDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  // Format preset toggle — Set semantics, but we copy before mutating so
  // React sees a new reference.
  //
  // 16:9 is the included base format and CANNOT be deselected — every
  // job ships with the landscape version. The 1:1 and 9:16 formats are
  // optional add-ons. We guard against deselecting 16:9 here as a
  // last-resort safety net; the UI also disables clicking the 16:9 chip
  // (see the format pill rendering below), but this catches any
  // programmatic attempt or future code paths that miss the lock.
  const toggleFormat = (id) => {
    if (id === '16:9') return; // locked — base format
    setSelectedFormats(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Live cost breakdown — mirrors the server-side getCostBreakdown in
  // videoConfig.js. Schedule keys match the source of truth:
  //   base, agentBranding, listingDetails, voiceover, formatAddons
  // Backend recomputes the authoritative total at job creation (see
  // calculateVideoCost in videoController.createVideoJob), so any tiny
  // client/server drift just affects the preview — what actually gets
  // charged is whatever the server computes.
  //
  // Format pricing is tiered:
  //   16:9 alone        → included in base, no extra charge
  //   16:9 + 1 extra    → +formatAddons.oneExtra (10)
  //   16:9 + both extras → +formatAddons.bothExtras (15, bundle saves 5)
  const computeCostBreakdown = () => {
    if (!videoOptions) return { lineItems: [], total: 0 };
    const schedule = videoOptions.pricing?.schedule || {};
    const lines = [];

    // Base — every job pays this; getCostBreakdown marks it `included: true`.
    const baseCost = schedule.base ?? 0;
    if (baseCost > 0) {
      lines.push({ label: 'Premium AI Video (16:9)', cost: baseCost });
    }

    // Overlay add-ons. Each entry maps a toggle id (which equals the
    // backend's options field name) to its pricing schedule key and
    // the human-readable label. Order matches the JSX checkbox loop.
    const overlays = [
      { id: 'showListingData', label: 'Listing Details', scheduleKey: 'listingDetails' },
      { id: 'showAgentBranding', label: 'Agent Branding', scheduleKey: 'agentBranding' },
    ];
    overlays.forEach(({ id, label, scheduleKey }) => {
      if (!overlayToggles[id]) return;
      const cost = schedule[scheduleKey] ?? 0;
      if (cost > 0) lines.push({ label, cost });
    });

    // Format bundle — one line item describing the extras the agent added.
    // Falls back to per-format pricing (oneExtra * count) if the backend's
    // schedule.formatAddons isn't present (older API version, defensive).
    const has1x1 = selectedFormats.has('1:1');
    const has9x16 = selectedFormats.has('9:16');
    const formatAddons = schedule.formatAddons || {};
    if (has1x1 && has9x16) {
      const cost = formatAddons.bothExtras ?? 0;
      if (cost > 0) lines.push({ label: 'Add Square + Vertical', cost });
    } else if (has1x1) {
      const cost = formatAddons.oneExtra ?? 0;
      if (cost > 0) lines.push({ label: 'Add Square (1:1)', cost });
    } else if (has9x16) {
      const cost = formatAddons.oneExtra ?? 0;
      if (cost > 0) lines.push({ label: 'Add Vertical (9:16)', cost });
    }

    const total = lines.reduce((sum, l) => sum + l.cost, 0);
    return { lineItems: lines, total };
  };

  // Submit gate — true when the agent can hit Generate without an error.
  // Checks: photo count within bounds, at least one format selected,
  // music chosen (when tracks exist), enough points in balance, not
  // already submitting.
  //
  // motionStyle is intentionally NOT gated here. The backend defaults
  // it server-side (MOTION_STYLES fallback to DEFAULT_MOTION_STYLE in
  // videoController.createVideoJob), so sending an empty string or
  // undefined is safe — checking it on the frontend just creates a
  // silent gate when the /options response is missing the field.
  const isSubmitDisabled = () => {
    if (!videoOptions) { console.log('[gate] no videoOptions'); return true; }
    if (submitting) { console.log('[gate] submitting'); return true; }
    const min = videoOptions.limits?.minPhotos ?? 5;
    const max = videoOptions.limits?.maxPhotos ?? 15;
    if (selectedPhotos.length < min) { console.log('[gate] photos too few', selectedPhotos.length, '<', min); return true; }
    if (selectedPhotos.length > max) { console.log('[gate] photos too many', selectedPhotos.length, '>', max); return true; }
    if (selectedFormats.size === 0) { console.log('[gate] no formats'); return true; }
    const hasTracks = (videoOptions.musicTracks?.length || 0) > 0;
    if (hasTracks && !musicTrack) { console.log('[gate] tracks exist but none picked'); return true; }
    const { total } = computeCostBreakdown();
    const balance = videoOptions.agent?.pointsBalance ?? 0;
    if (balance < total) { console.log('[gate] insufficient', balance, '<', total); return true; }
    return false;
  };

  // POST /api/videos/jobs with the full configuration. Body shape matches
  // what the controller destructures:
  //   { listingId, options: { ... } }
  //
  // Options schema (see videoController.createVideoJob lines 297-314):
  //   - formatPresets:      string[] of "9:16" | "1:1" | "16:9"
  //   - musicTrackId:       ObjectId from musicTracks[]._id
  //   - motionStyle:        seeded from selectors.defaultMotionStyle
  //   - selectedPhotoUrls:  ordered array of photo URLs
  //   - showAgentBranding:  bool → +VIDEO_PRICING.agentBranding pts
  //   - showListingData:    bool → +VIDEO_PRICING.listingDetails pts
  //   - voiceoverEnabled:   not exposed in this UI yet (own flow)
  //
  // Backend re-computes cost with calculateVideoCost() and is the source
  // of truth for points deduction — this payload is a request, not a
  // commitment.
  const handleSubmitVideoJob = async () => {
    if (isSubmitDisabled()) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const result = await propertyService.createVideoJob({
        listingId: videoModalProperty._id,
        options: {
          formatPresets: Array.from(selectedFormats),
          musicTrackId: musicTrack || null,
          motionStyle,
          selectedPhotoUrls: selectedPhotos,
          showListingData: !!overlayToggles.showListingData,
          showAgentBranding: !!overlayToggles.showAgentBranding,
        },
      });

      // Stash whatever the API returned. The shape varies (could be the
      // raw VideoJob, could be { job: ... }). We normalize below when
      // rendering, so just keep the whole thing for now.
      setSubmittedJob(result || null);

      // Refresh points balance. /api/videos/options is the easiest path
      // since it already returns `agent.pointsBalance`. Errors are
      // non-fatal — if it fails, we just don't show the refreshed
      // balance, the job submission itself succeeded.
      try {
        const refreshed = await propertyService.getVideoOptions();
        const newBalance = refreshed?.agent?.pointsBalance;
        if (typeof newBalance === 'number') {
          setPostSubmitBalance(newBalance);
          // Update the in-memory videoOptions so anything else using it
          // (e.g. the dropdown if the user reopens later) reflects the new
          // balance too.
          setVideoOptions(refreshed);
        }
      } catch (e) {
        console.warn('Failed to refresh balance after submit:', e);
      }

      // Notify parent so any usage badges / points displays in the surrounding
      // shell refresh too. The job render itself happens asynchronously on
      // the worker — modal stays open in the success state.
      if (onPropertyChanged) onPropertyChanged();

      // Swap modal body to the success state. The agent sees confirmation
      // that the job was accepted, the points deducted, and what to expect
      // next (email + portal access when ready).
      setSubmitted(true);
    } catch (err) {
      console.error('Failed to create video job:', err);
      setSubmitError(
        err?.response?.data?.message || err?.message || 'Failed to start video generation'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Fetch properties when component mounts or filters/page change
  useEffect(() => {
    fetchProperties();
  }, [agentId, currentPage, filters]);

  const fetchProperties = async () => {
    try {
      setIsLoading(true);
  
      console.log('🔍 Fetching agent listings...');
      console.log('🔹 agentId:', agentId);
      console.log('🔹 filters:', filters);
  
      // Create a copy of filters without approvalStatus for API calls
      const { approvalStatus, ...apiFilters } = filters;
      
      const queryParams = {
        agent: agentId,
        page: currentPage,
        limit: 10,
        ...apiFilters
      };
  
      if (searchQuery.trim()) {
        queryParams.search = searchQuery.trim();
      }
  
      // Determine which API calls to make based on approvalStatus filter
      let approved = [];
      let pending = [];
      
      if (!approvalStatus || approvalStatus === 'approved') {
        const approvedRes = await propertyService.getAgentProperties(queryParams);
        console.log('✅ Approved properties response:', approvedRes);
        approved = (approvedRes?.data || []).map(prop => ({ 
          ...prop, 
          approvalStatus: 'approved' 
        }));
      }
      
      if (!approvalStatus || approvalStatus === 'pending') {
        const pendingRes = await propertyService.getPendingProperties(queryParams);
        console.log('✅ Pending properties response:', pendingRes);
        pending = (pendingRes?.data || []).map(prop => ({ 
          ...prop, 
          approvalStatus: 'pending' 
        }));
      }
  
      const all = [...approved, ...pending].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
  
      setProperties(all);
      setTotalPages(Math.ceil(all.length / 10));
    } catch (error) {
      console.error('🔥 Error fetching properties:', error);
      setError('Failed to load properties');
    } finally {
      setIsLoading(false);
    }
  };
  

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page
    fetchProperties();
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
  };

  const confirmDelete = (property) => {
    setPropertyToDelete(property);
    setShowDeleteModal(true);
    setDeleteError('');
  };

  const openPropertyDetails = (property) => {
    setSelectedProperty(property);
    setShowDetailsModal(true);
  };

  const closePropertyDetails = () => {
    setShowDetailsModal(false);
    setSelectedProperty(null);
  };

  const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      
      await propertyService.deleteProperty(propertyToDelete._id);
      
      // Close modal and refresh property list
      setShowDeleteModal(false);
      setPropertyToDelete(null);
      
      // Notify parent component
      if (onPropertyChanged) {
        onPropertyChanged();
      }
      
      // Refresh properties
      fetchProperties();
    } catch (error) {
      console.error('Error deleting property:', error);
      setDeleteError(`Failed to delete property: ${error.message || 'Unknown error'}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Generate pagination items
  const paginationItems = [];
  for (let i = 1; i <= totalPages; i++) {
    if (
      i === 1 || 
      i === totalPages || 
      (i >= currentPage - 1 && i <= currentPage + 1)
    ) {
      paginationItems.push(
        <button
          key={i}
          className={`pagination-number ${currentPage === i ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    } else if (
      i === currentPage - 2 || 
      i === currentPage + 2
    ) {
      paginationItems.push(
        <span key={i} className="pagination-ellipsis">
          ...
        </span>
      );
    }
  }

  return (
    <div style={{ color: '#111' }}>
      <style>{`
        /* Property List — modern light refresh matching admin aesthetic */
        .section-title {
          color: #111 !important;
          margin-bottom: 20px;
          font-size: 1.5rem;
          font-weight: 700;
          letter-spacing: -0.02em;
        }

        /* Page header */
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }
        .page-header h2 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #111;
          margin: 0;
          letter-spacing: -0.02em;
        }
        .boost-listing-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 11px 18px;
          background: linear-gradient(135deg, #f59e0b 0%, #ec4899 50%, #8b5cf6 100%);
          color: #ffffff;
          border: none;
          border-radius: 10px;
          font-size: 0.88rem;
          font-weight: 600;
          letter-spacing: 0.01em;
          cursor: pointer;
          position: relative;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(236, 72, 153, 0.25), 0 1px 2px rgba(0, 0, 0, 0.05);
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .boost-listing-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -75%;
          width: 50%;
          height: 100%;
          background: linear-gradient(
            120deg,
            transparent 0%,
            rgba(255, 255, 255, 0.35) 50%,
            transparent 100%
          );
          transform: skewX(-20deg);
          transition: left 0.6s ease;
          pointer-events: none;
        }
        .boost-listing-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 18px rgba(236, 72, 153, 0.35), 0 2px 4px rgba(0, 0, 0, 0.06);
        }
        .boost-listing-btn:hover::before {
          left: 125%;
        }
        .boost-listing-btn:active {
          transform: translateY(0);
        }
        .boost-listing-btn svg {
          fill: #ffffff;
        }

        /* Filter bar */
        .filter-container {
          background-color: #ffffff !important;
          border: 1px solid #e5e7eb !important;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03) !important;
          border-radius: 14px;
          padding: 18px 20px !important;
          margin-bottom: 16px;
        }
        .grid-3-cols {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 14px;
        }

        /* Micro labels */
        .form-label {
          color: #6b7280 !important;
          font-weight: 600;
          font-size: 0.68rem;
          margin-bottom: 6px;
          display: block;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }

        /* Inputs */
        .form-control {
          background-color: #ffffff !important;
          border: 1px solid #e5e7eb !important;
          color: #111 !important;
          padding: 9px 11px;
          border-radius: 9px;
          transition: border-color 0.12s, box-shadow 0.12s;
          width: 100%;
          font-size: 0.9rem;
          font-family: inherit;
        }
        .form-control:focus {
          border-color: #111 !important;
          background-color: #ffffff !important;
          outline: none;
          box-shadow: 0 0 0 3px rgba(17, 17, 17, 0.06);
        }
        .form-control option {
          background-color: #ffffff;
          color: #111;
        }
        .form-control::placeholder {
          color: #9ca3af;
        }

        /* Search inside input */
        .search-container {
          position: relative;
          display: flex;
          width: 100%;
        }
        .search-container input {
          padding-right: 38px;
          width: 100%;
        }
        .search-button {
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #9ca3af !important;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          padding: 0;
          height: 24px;
          width: 24px;
          transition: color 0.12s;
        }
        .search-button:hover {
          color: #111 !important;
        }

        /* Table shell */
        .table-responsive {
          background-color: #ffffff !important;
          border: 1px solid #e5e7eb !important;
          border-radius: 14px;
          padding: 0;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
          overflow-x: auto;
        }
        .data-table {
          color: #111 !important;
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          background-color: transparent !important;
        }
        .data-table * {
          background-color: transparent !important;
        }
        .data-table thead {
          background-color: #fafafa !important;
        }
        .data-table th {
          color: #6b7280 !important;
          border-bottom: 1px solid #e5e7eb !important;
          background-color: #fafafa !important;
          padding: 12px 16px !important;
          text-align: left;
          font-weight: 600;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }
        .data-table th:first-child {
          border-top-left-radius: 14px;
        }
        .data-table th:last-child {
          border-top-right-radius: 14px;
        }
        .data-table tbody tr {
          background-color: transparent !important;
          transition: background-color 0.12s;
          border: none !important;
        }
        .data-table tbody tr:hover {
          background-color: #f9fafb !important;
        }
        .data-table tbody tr:hover td {
          background-color: #f9fafb !important;
        }
        .data-table td {
          color: #111 !important;
          border-bottom: 1px solid #f0f0f0 !important;
          background-color: transparent !important;
          padding: 14px 16px !important;
          border-left: none !important;
          border-right: none !important;
          border-top: none !important;
          font-size: 0.9rem;
        }
        .data-table tbody tr:last-child td {
          border-bottom: none !important;
        }

        /* Property row */
        .property-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .property-image {
          width: 56px;
          height: 56px;
          border-radius: 8px;
          overflow: hidden;
          background-color: #f4f4f5;
          flex-shrink: 0;
          border: 1px solid #e5e7eb;
        }
        .property-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .property-details {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .property-address {
          color: #111 !important;
          font-weight: 600;
          font-size: 0.92rem;
          letter-spacing: -0.005em;
        }
        .property-address:hover {
          color: #000 !important;
        }
        .property-neighborhood {
          color: #6b7280 !important;
          font-size: 0.82rem;
        }
        .text-text-medium {
          color: #6b7280 !important;
        }
        .font-medium {
          color: #111 !important;
        }
        .text-sm {
          color: #374151 !important;
        }

        /* Action button "links" inside table */
        .btn-link {
          color: #374151 !important;
          text-decoration: none;
          background: none;
          border: none;
          cursor: pointer;
          padding: 5px 10px;
          border-radius: 6px;
          transition: all 0.12s;
          font-size: 0.82rem;
          font-weight: 500;
        }
        .btn-link:hover {
          background-color: #f4f4f5;
          color: #111 !important;
        }
        .text-blue {
          color: #111 !important;
        }
        .text-blue:hover {
          color: #000 !important;
          background-color: #f4f4f5 !important;
        }
        .text-red {
          color: #dc2626 !important;
        }
        .text-red:hover {
          color: #b91c1c !important;
          background-color: #fef2f2 !important;
        }

        /* Compact CTAs inside the Actions column.
           These match the visual language of the top "Boost Listings" button
           but in a table-friendly size — smaller padding, smaller font, and
           a tighter shadow so they don't overpower row content.

           Two modifiers:
             --video  → sage palette (matches Lauft's brand secondary)
             --boost  → same warm gradient as the top boost button
         */
        .table-action-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 7px 12px;
          border: none;
          border-radius: 8px;
          font-size: 0.78rem;
          font-weight: 600;
          letter-spacing: 0.01em;
          color: #ffffff;
          cursor: pointer;
          position: relative;
          overflow: hidden;
          transition: transform 0.12s ease, box-shadow 0.12s ease;
          white-space: nowrap;
        }
        .table-action-btn svg {
          flex-shrink: 0;
        }
        /* Shared shine sweep (same as .boost-listing-btn::before) */
        .table-action-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -75%;
          width: 50%;
          height: 100%;
          background: linear-gradient(
            120deg,
            transparent 0%,
            rgba(255, 255, 255, 0.35) 50%,
            transparent 100%
          );
          transform: skewX(-20deg);
          transition: left 0.6s ease;
          pointer-events: none;
        }
        .table-action-btn:hover::before {
          left: 125%;
        }
        .table-action-btn:active {
          transform: translateY(0);
        }

        /* Video — portal blue palette (matches the "For Sale" badge and
           other blue accents used across the agent portal: #2563eb → #1d4ed8) */
        .table-action-btn--video {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          box-shadow: 0 2px 8px rgba(37, 99, 235, 0.28), 0 1px 2px rgba(0, 0, 0, 0.05);
        }
        .table-action-btn--video:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.38), 0 2px 4px rgba(0, 0, 0, 0.06);
        }

        /* Boost — matches the top boost button's gradient */
        .table-action-btn--boost {
          background: linear-gradient(135deg, #f59e0b 0%, #ec4899 50%, #8b5cf6 100%);
          box-shadow: 0 2px 8px rgba(236, 72, 153, 0.25), 0 1px 2px rgba(0, 0, 0, 0.05);
        }
        .table-action-btn--boost:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(236, 72, 153, 0.35), 0 2px 4px rgba(0, 0, 0, 0.06);
        }
        .table-action-btn--boost svg {
          fill: #ffffff;
        }

        /* Pagination */
        .pagination-container {
          margin-top: 20px;
          padding: 16px 20px;
          border-top: 1px solid #f0f0f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
        }
        .pagination-info {
          color: #6b7280 !important;
          font-size: 0.82rem;
        }
        .pagination-controls {
          display: flex;
          gap: 4px;
          align-items: center;
        }
        .pagination-arrow,
        .pagination-number {
          background-color: #ffffff;
          color: #374151;
          border: 1px solid #e5e7eb;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 0.82rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.12s;
        }
        .pagination-arrow:hover:not(:disabled),
        .pagination-number:hover:not(.active) {
          background-color: #f9fafb;
          border-color: #d1d5db;
        }
        .pagination-number.active {
          background-color: #111;
          color: #ffffff;
          border-color: #111;
        }
        .pagination-arrow:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .pagination-ellipsis {
          color: #9ca3af;
          padding: 0 4px;
        }

        /* Empty-state content container */
        .content-container {
          background-color: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          padding: 48px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
        }

        /* Loading spinner */
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .loading-spinner {
          border: 2.5px solid #f0f0f0 !important;
          border-top-color: #111 !important;
          border-radius: 50%;
          width: 28px;
          height: 28px;
          animation: spin 0.7s linear infinite;
          display: inline-block;
          margin: 0 auto;
        }

        /* Modals */
        .modal-overlay {
          background-color: rgba(0, 0, 0, 0.4) !important;
          backdrop-filter: blur(4px);
        }
        .modal-dialog {
          background-color: #ffffff !important;
          border: 1px solid #e5e7eb !important;
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
        }
        .modal-header h3 {
          color: #111 !important;
          font-weight: 700;
          letter-spacing: -0.01em;
        }
        .modal-close {
          color: #9ca3af !important;
          transition: color 0.12s;
        }
        .modal-close:hover {
          color: #111 !important;
        }
        .modal-body {
          color: #111 !important;
        }
        .modal-body p {
          color: #374151 !important;
        }

        /* Buttons (inside modals) */
        .btn {
          transition: all 0.12s;
        }
        .btn-secondary {
          background-color: #ffffff !important;
          color: #111 !important;
          border: 1px solid #e5e7eb !important;
          padding: 9px 18px;
          border-radius: 9px;
          font-weight: 600;
          font-size: 0.85rem;
        }
        .btn-secondary:hover {
          background-color: #f9fafb !important;
          border-color: #d1d5db !important;
        }
        .btn-danger {
          background-color: #dc2626 !important;
          color: #ffffff !important;
          border: 1px solid #dc2626 !important;
          padding: 9px 18px;
          border-radius: 9px;
          font-weight: 600;
          font-size: 0.85rem;
        }
        .btn-danger:hover:not(:disabled) {
          background-color: #b91c1c !important;
          border-color: #b91c1c !important;
          transform: translateY(-1px);
        }
        .btn-danger:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Alerts */
        .alert {
          padding: 11px 14px;
          border-radius: 9px;
          font-size: 0.875rem;
          font-weight: 500;
        }
        .alert-danger {
          background-color: #fef2f2 !important;
          color: #b91c1c !important;
          border: 1px solid #fecaca !important;
        }

        /* Status badges */
        .status-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 999px;
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.02em;
        }
        .status-pending {
          background-color: #fff7ed;
          color: #c2410c;
          border: 1px solid #fed7aa;
        }
        .status-approved {
          background-color: #ecfdf5;
          color: #047857;
          border: 1px solid #a7f3d0;
        }
      `}</style>
      
      {error && (
        <div className="alert alert-danger mb-4">
          {error}
        </div>
      )}
      
      {/* Filters and Search */}
      <div className="filter-container p-4 mb-4">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '20px', alignItems: 'end' }}>
          {/* Search */}
          <div className="form-group">
  <form onSubmit={handleSearchSubmit}>
    <label className="form-label">Search Properties</label>
    <div className="search-container">
      <input
        type="text"
        className="form-control"
        placeholder="Search..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
      <button type="submit" className="search-button">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
      </button>
    </div>
  </form>
</div>
          
          {/* Status Filter */}
          <div className="form-group">
            <label className="form-label">Status</label>
            <select
              name="approvalStatus"
              value={filters.approvalStatus}
              onChange={handleFilterChange}
              className="form-control"
            >
              <option value="">All Statuses</option>
              <option value="pending">In Review</option>
              <option value="approved">Active</option>
            </select>
          </div>
          
          {/* Property Type Filter */}
          <div className="form-group">
            <label className="form-label">Property Type</label>
            <select
              name="propertyType"
              value={filters.propertyType}
              onChange={handleFilterChange}
              className="form-control"
            >
              <option value="">All Types</option>
              <option value="House">House</option>
              <option value="Condo">Condo</option>
              <option value="Apartment">Apartment</option>
              <option value="Townhouse">Townhouse</option>
              <option value="Multifamily">Multi-Family</option>
              <option value="ManufacturedMobile">Manufactured/Mobile</option>
            </select>
          </div>

          {/* Boost Listing Button */}
          <div className="form-group">
            <label className="form-label" style={{ visibility: 'hidden' }}>.</label>
            <button
              className="boost-listing-btn"
              onClick={() => setShowBoostComingSoon(true)}
              style={{ whiteSpace: 'nowrap', width: '100%' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M13 2L4.09 12.97c-.59.73-.06 1.81.88 1.81H11l-1 7.22c-.13.93 1.06 1.43 1.62.69L20 11.36c.59-.73.06-1.81-.88-1.81H13l1-7.22c.13-.93-1.06-1.43-1.62-.69z"/>
              </svg>
              Boost Listings
            </button>
          </div>
        </div>
      </div>
      
      {/* Properties Table */}
      {isLoading ? (
        <div className="text-center py-4">
          <div className="loading-spinner"></div>
          <p className="mt-2 text-text-medium">Loading properties...</p>
        </div>
      ) : properties.length > 0 ? (
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Details</th>
                <th>Price</th>
                <th>Status</th>
                <th>Video</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {properties.map((property) => (
                <tr key={property._id}>
                  <td className="property-cell">
                    <div className="property-info">
                      {property.mainImage && (
                        <div className="property-image">
                          <img src={property.mainImage} alt={property.address} />
                        </div>
                      )}
                      <div className="property-details">
                        <div 
                          className="property-address" 
                          onClick={() => openPropertyDetails(property)}
                          style={{ cursor: 'pointer', textAlign: 'left' }}
                        >
                          {property.address}
                          {property.dataSource === 'manual' && (
                            <span style={{
                              marginLeft: '8px',
                              padding: '2px 8px',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              borderRadius: '10px',
                              background: 'rgba(107, 114, 128, 0.12)',
                              color: '#4b5563',
                              border: '1px solid rgba(107, 114, 128, 0.25)',
                              verticalAlign: 'middle',
                              letterSpacing: '0.02em'
                            }}>
                              MANUAL
                            </span>
                          )}
                        </div>
                        <div className="property-neighborhood" style={{ textAlign: 'left' }}>
                          {property.csz || ''}
                        </div>
                        {/* Source-specific affordances sit right under the
                            address block where the listing identity is.
                            - Manual listings: Edit / Delete inline links
                            - MLS listings: an "MLS-synced" tag for provenance
                            Keeping these here (rather than in the Actions
                            column) lets Actions stay focused on the new
                            video / boost feature CTAs. */}
                        {property.dataSource === 'manual' ? (
                          <div style={{
                            marginTop: '6px',
                            display: 'flex',
                            gap: '12px',
                            alignItems: 'center'
                          }}>
                            <button
                              onClick={() => {
                                alert("To edit a listing, please send an email to listings@lauft.app");
                              }}
                              className="btn-link text-blue"
                              style={{ fontSize: '0.78rem' }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => confirmDelete(property)}
                              className="btn-link text-red"
                              style={{ fontSize: '0.78rem' }}
                            >
                              Delete
                            </button>
                          </div>
                        ) : (
                          <div style={{
                            marginTop: '6px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: '#475569',
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase'
                          }}>
                            <svg
                              width="11"
                              height="11"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.4"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <path d="M21 12a9 9 0 1 1-9-9" />
                              <polyline points="21 3 21 9 15 9" />
                            </svg>
                            MLS-synced
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px',
                      marginBottom: '6px'
                    }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                        padding: '3px 9px',
                        background: 'rgba(15, 23, 42, 0.05)',
                        borderRadius: '999px',
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        color: '#0f172a',
                        lineHeight: 1.4
                      }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M2 20v-8a4 4 0 0 1 4-4h12a4 4 0 0 1 4 4v8" />
                          <path d="M2 20h20" />
                          <path d="M6 8V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v3" />
                        </svg>
                        {property.bedrooms ?? '—'} bd
                      </span>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                        padding: '3px 9px',
                        background: 'rgba(15, 23, 42, 0.05)',
                        borderRadius: '999px',
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        color: '#0f172a',
                        lineHeight: 1.4
                      }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M3 12V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1" />
                          <path d="M2 12h20v3a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4z" />
                          <path d="M6 19v2" />
                          <path d="M18 19v2" />
                        </svg>
                        {property.bathrooms ?? '—'} ba
                      </span>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                        padding: '3px 9px',
                        background: 'rgba(15, 23, 42, 0.05)',
                        borderRadius: '999px',
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        color: '#0f172a',
                        lineHeight: 1.4
                      }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <path d="M3 9h6M3 15h6M9 3v6M15 3v6M9 21v-6M15 21v-6M21 9h-6M21 15h-6" />
                        </svg>
                        {property.sqft ? Number(property.sqft).toLocaleString() : '—'} sqft
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {property.propertyType && (
                        <span style={{
                          fontSize: '0.75rem',
                          color: '#475569',
                          fontWeight: 500
                        }}>
                          {property.propertyType}
                        </span>
                      )}
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        letterSpacing: '0.02em',
                        textTransform: 'uppercase',
                        background: property.listingType === 'purchase'
                          ? 'rgba(37, 99, 235, 0.1)'
                          : 'rgba(168, 85, 247, 0.1)',
                        color: property.listingType === 'purchase'
                          ? '#1d4ed8'
                          : '#7e22ce'
                      }}>
                        {property.listingType === 'purchase' ? 'For Sale' : 'For Rent'}
                      </span>
                    </div>
                  </td>
                  <td>{property.price}</td>
                  <td>
                    {property.approvalStatus === 'pending' ? (
                      <span className="status-badge status-pending">In Review</span>
                    ) : (
                      <span className="status-badge status-approved">Active</span>
                    )}
                  </td>
                  {/* Video cell — Chunk 1: placeholder thumbnail. Chunk 2
                      wires this up to fetch the most recent completed video
                      for this property via a batched API call. For now we
                      render a neutral "No video yet" state with consistent
                      sizing so the column doesn't jump around. */}
                  <td>
                    <div
                      style={{
                        width: '64px',
                        height: '36px',
                        borderRadius: '6px',
                        background: 'rgba(15, 23, 42, 0.04)',
                        border: '1px dashed rgba(15, 23, 42, 0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#94a3b8',
                        fontSize: '0.65rem',
                        letterSpacing: '0.02em',
                        textTransform: 'uppercase',
                        fontWeight: 600
                      }}
                      title="No video generated yet"
                    >
                      None
                    </div>
                  </td>
                  <td>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '8px'
                      }}
                    >
                      {/* Boost — same gradient as the top Boost Listings
                          button. Sized smaller for table use. Stacks on top
                          so it sits closer to the top of the row. */}
                      <button
                        onClick={() => setShowBoostComingSoon(true)}
                        className="table-action-btn table-action-btn--boost"
                        title="Boost this listing's visibility"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path d="M13 2L4.09 12.97c-.59.73-.06 1.81.88 1.81H11l-1 7.22c-.13.93 1.06 1.43 1.62.69L20 11.36c.59-.73.06-1.81-.88-1.81H13l1-7.22c.13-.93-1.06-1.43-1.62-.69z"/>
                        </svg>
                        Boost
                      </button>

                      {/* Generate Video — sage gradient, matches Lauft brand.
                          Same visual language as Boost (gradient + shine sweep
                          on hover) so the two row CTAs feel like a coherent
                          pair. */}
                      <button
                        onClick={() => openVideoModal(property)}
                        className="table-action-btn table-action-btn--video"
                        title="Generate an AI listing video from this property's photos"
                      >
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <polygon points="23 7 16 12 23 17 23 7" />
                          <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                        </svg>
                        Generate Video
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {/* Pagination */}
          <div className="pagination-container">
            <div className="pagination-info">
              Showing {properties.length} of {totalPages * 10} properties
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-arrow"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                &laquo; Prev
              </button>
              
              {paginationItems}
              
              <button
                className="pagination-arrow"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next &raquo;
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="content-container text-center py-4">
          <p className="text-text-medium">No listings match this criteria. MLS-synced listings appear here automatically — manually-uploaded listings can be added with the button above.</p>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-dialog">
            <div className="modal-header">
              <h3 className="text-lg font-medium">Confirm Delete</h3>
              <button 
                onClick={() => setShowDeleteModal(false)}
                className="modal-close"
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              {deleteError && (
                <div className="alert alert-danger mb-4">
                  {deleteError}
                </div>
              )}
              <p>
                Are you sure you want to delete the property at:<br />
                <strong>{propertyToDelete?.address}</strong>?
              </p>
              <p className="mt-2 text-text-medium">
                This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="btn btn-secondary mr-2"
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="btn btn-danger"
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting...' : 'Delete Property'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Property Details Modal */}
      <PropertyDetailsModal 
        property={selectedProperty}
        isOpen={showDetailsModal}
        onClose={closePropertyDetails}
      />

      {/* Boost Listing — Coming Soon */}
      {showBoostComingSoon && createPortal(
        <div
          onClick={() => setShowBoostComingSoon(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.55)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            padding: '20px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '420px',
              width: '100%',
              background: '#ffffff',
              borderRadius: '14px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)'
            }}
          >
            <div style={{
              padding: '32px 28px 24px 28px',
              background: 'linear-gradient(135deg, #f59e0b 0%, #ec4899 50%, #8b5cf6 100%)',
              color: '#ffffff',
              textAlign: 'center',
              position: 'relative'
            }}>
              <button
                onClick={() => setShowBoostComingSoon(false)}
                aria-label="Close"
                style={{
                  position: 'absolute',
                  top: '12px',
                  right: '14px',
                  background: 'rgba(255,255,255,0.18)',
                  border: 'none',
                  color: '#ffffff',
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  fontSize: '1.1rem',
                  lineHeight: 1,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                &times;
              </button>
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '12px'
              }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="#ffffff" aria-hidden="true">
                  <path d="M13 2L4.09 12.97c-.59.73-.06 1.81.88 1.81H11l-1 7.22c-.13.93 1.06 1.43 1.62.69L20 11.36c.59-.73.06-1.81-.88-1.81H13l1-7.22c.13-.93-1.06-1.43-1.62-.69z"/>
                </svg>
              </div>
              <h3 style={{
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: 700,
                letterSpacing: '-0.01em'
              }}>
                Boost Listing
              </h3>
              <div style={{
                display: 'inline-block',
                marginTop: '8px',
                padding: '3px 10px',
                background: 'rgba(255,255,255,0.22)',
                borderRadius: '999px',
                fontSize: '0.7rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase'
              }}>
                Coming Soon
              </div>
            </div>
            <div style={{
              padding: '20px 28px 24px 28px',
              textAlign: 'center',
              color: '#374151'
            }}>
              <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: 1.55 }}>
                Soon you'll be able to boost your listings to the top of user feeds and reach more swiping users in your area.
              </p>
              <p style={{ margin: '10px 0 0 0', fontSize: '0.85rem', color: '#6b7280' }}>
                We'll let you know the moment it's ready.
              </p>
              <button
                onClick={() => setShowBoostComingSoon(false)}
                className="btn btn-secondary"
                style={{ marginTop: '20px', minWidth: '120px' }}
              >
                Got it
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ─────────────────────────────────────────────────────────────────
          Video Generation Modal — Chunk 2: full configuration UI.

          What this renders:
            - Header with property address
            - Two-column body:
              · Left: photo picker (grid + drag-to-reorder ordered strip,
                bounded by limits.minPhotos / limits.maxPhotos from /options)
              · Right: format presets, motion style, music track, overlay
                add-on toggles, live cost breakdown + agent points balance
                + monthly usage indicator
            - Footer: selection summary, Cancel, and Generate with live
              total point cost; disabled until selection is valid and the
              agent has sufficient points

          Submit flow: POSTs to /api/videos/jobs (via propertyService).
          Backend re-validates everything against videoConfig.js — frontend
          never holds the source of truth for limits or pricing.

          Pattern matches the Boost modal: createPortal to document.body,
          backdrop blur, zIndex 999999, click-outside-to-close, stopPropagation
          on the inner panel.
          ───────────────────────────────────────────────────────────────── */}
      {showVideoModal && videoModalProperty && createPortal(
        <div
          onClick={closeVideoModal}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.55)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            padding: '20px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '1080px',
              width: '100%',
              maxHeight: '92vh',
              background: '#ffffff',
              borderRadius: '14px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)'
            }}
          >
            {/* Header — sage gradient to anchor the video-generation feature
                visually to the rest of the Lauft brand palette. Property
                address goes here so the agent knows what they're generating
                a video for at a glance. */}
            <div style={{
              padding: '24px 28px 20px 28px',
              background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
              color: '#ffffff',
              position: 'relative',
              flexShrink: 0
            }}>
              <button
                onClick={closeVideoModal}
                aria-label="Close"
                style={{
                  position: 'absolute',
                  top: '14px',
                  right: '16px',
                  background: 'rgba(255,255,255,0.18)',
                  border: 'none',
                  color: '#ffffff',
                  width: '30px',
                  height: '30px',
                  borderRadius: '50%',
                  fontSize: '1.2rem',
                  lineHeight: 1,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                &times;
              </button>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '8px',
                fontSize: '0.7rem',
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                opacity: 0.85
              }}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polygon points="23 7 16 12 23 17 23 7" />
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                </svg>
                Generate AI Video
              </div>
              <h2 style={{
                margin: 0,
                fontSize: '1.4rem',
                fontWeight: 600,
                lineHeight: 1.3,
                paddingRight: '40px'
              }}>
                {videoModalProperty.address || 'Listing'}
              </h2>
              {videoModalProperty.csz && (
                <div style={{
                  marginTop: '4px',
                  fontSize: '0.85rem',
                  opacity: 0.85
                }}>
                  {videoModalProperty.csz}
                </div>
              )}
            </div>

            {/* Body — two-column layout. Left: photo picker. Right:
                option selectors + cost breakdown. The whole body scrolls
                as one unit; cost sits right after the option toggles so
                agents see it without hunting. On narrow viewports the
                columns stack vertically via flexWrap.

                When `submitted` is true, the body swaps to a success state
                — the configuration UI is gone, replaced by a confirmation
                that the job was accepted, what to expect next, and the
                updated points balance. Footer also swaps to a single
                "Done" button. */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              background: '#fafaf8'
            }}>
              {submitted ? (
                <div style={{
                  padding: '48px 36px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  maxWidth: '500px',
                  margin: '0 auto'
                }}>
                  {/* Success checkmark badge */}
                  <div style={{
                    width: '72px',
                    height: '72px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '24px',
                    boxShadow: '0 8px 24px rgba(37, 99, 235, 0.25)'
                  }}>
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none"
                         stroke="white" strokeWidth="3" strokeLinecap="round"
                         strokeLinejoin="round" aria-hidden="true">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>

                  <h3 style={{
                    margin: '0 0 8px',
                    fontSize: '1.35rem',
                    fontWeight: 600,
                    color: '#0f172a',
                    letterSpacing: '-0.01em'
                  }}>
                    Your video is being created
                  </h3>

                  <p style={{
                    margin: '0 0 28px',
                    fontSize: '0.95rem',
                    color: '#475569',
                    lineHeight: 1.6,
                    maxWidth: '420px'
                  }}>
                    This usually takes <strong>3 – 5 minutes</strong>. You
                    can leave this page — we'll email you when it's ready,
                    and you'll be able to view and download it from your
                    portal.
                  </p>

                  {/* Status pills row — visual reassurance of the two
                      delivery paths (email + portal). Sized to read as
                      "these are things that will happen," not as actions. */}
                  <div style={{
                    display: 'flex',
                    gap: '10px',
                    marginBottom: '32px',
                    flexWrap: 'wrap',
                    justifyContent: 'center'
                  }}>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 14px',
                      background: '#ffffff',
                      border: '1px solid rgba(15, 23, 42, 0.08)',
                      borderRadius: '999px',
                      fontSize: '0.78rem',
                      color: '#334155',
                      fontWeight: 500
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                           stroke="#2563eb" strokeWidth="2" strokeLinecap="round"
                           strokeLinejoin="round" aria-hidden="true">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                      Email when ready
                    </div>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 14px',
                      background: '#ffffff',
                      border: '1px solid rgba(15, 23, 42, 0.08)',
                      borderRadius: '999px',
                      fontSize: '0.78rem',
                      color: '#334155',
                      fontWeight: 500
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                           stroke="#2563eb" strokeWidth="2" strokeLinecap="round"
                           strokeLinejoin="round" aria-hidden="true">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                        <line x1="8" y1="21" x2="16" y2="21" />
                        <line x1="12" y1="17" x2="12" y2="21" />
                      </svg>
                      Available in your portal
                    </div>
                  </div>

                  {/* Receipt row — points deducted + new balance. Renders
                      whatever the cost breakdown computed before submission
                      (we already have it in scope from computeCostBreakdown).
                      The new balance is from the post-submit options refetch;
                      if it's not available yet (slow API), we hide the
                      "new balance" line gracefully. */}
                  <div style={{
                    width: '100%',
                    maxWidth: '380px',
                    background: '#ffffff',
                    border: '1px solid rgba(15, 23, 42, 0.08)',
                    borderRadius: '10px',
                    padding: '16px 18px',
                    textAlign: 'left'
                  }}>
                    {(() => {
                      const breakdown = computeCostBreakdown();
                      return (
                        <>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'baseline',
                            fontSize: '0.85rem',
                            color: '#475569',
                            marginBottom: postSubmitBalance != null ? '8px' : '0'
                          }}>
                            <span>Points charged</span>
                            <span style={{ color: '#0f172a', fontWeight: 600 }}>
                              {breakdown.total} pts
                            </span>
                          </div>
                          {postSubmitBalance != null && (
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'baseline',
                              fontSize: '0.85rem',
                              color: '#475569',
                              paddingTop: '8px',
                              borderTop: '1px solid rgba(15, 23, 42, 0.06)'
                            }}>
                              <span>Your new balance</span>
                              <span style={{ color: '#0f172a', fontWeight: 600 }}>
                                {postSubmitBalance.toLocaleString()} pts
                              </span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              ) : videoOptionsLoading && !videoOptions ? (
                <div style={{
                  padding: '60px 28px',
                  textAlign: 'center',
                  color: '#64748b',
                  fontSize: '0.9rem'
                }}>
                  Loading video options...
                </div>
              ) : videoOptionsError ? (
                <div style={{
                  margin: '28px',
                  padding: '16px 18px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '10px',
                  color: '#991b1b',
                  fontSize: '0.875rem'
                }}>
                  <strong style={{ display: 'block', marginBottom: 4 }}>
                    Couldn't load video options
                  </strong>
                  {videoOptionsError}
                  <button
                    onClick={() => { setVideoOptions(null); fetchVideoOptions(); }}
                    style={{
                      marginTop: '10px',
                      background: 'transparent',
                      border: '1px solid #991b1b',
                      color: '#991b1b',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      cursor: 'pointer'
                    }}
                  >
                    Retry
                  </button>
                </div>
              ) : videoOptions && (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0',
                  alignItems: 'stretch'
                }}>
                  {/* ────────── LEFT COLUMN: Photo picker ────────── */}
                  <div style={{
                    flex: '1 1 540px',
                    minWidth: '320px',
                    padding: '24px 24px 24px 28px',
                    borderRight: '1px solid rgba(15, 23, 42, 0.06)'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      marginBottom: '6px'
                    }}>
                      <h3 style={{
                        margin: 0,
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        color: '#0f172a'
                      }}>
                        Photos
                      </h3>
                      <span style={{
                        fontSize: '0.78rem',
                        color: selectedPhotos.length < (videoOptions.limits?.minPhotos ?? 5)
                          ? '#b45309'
                          : '#64748b',
                        fontWeight: 500
                      }}>
                        {selectedPhotos.length} / {videoOptions.limits?.maxPhotos ?? 15} selected
                      </span>
                    </div>
                    <p style={{
                      margin: '0 0 14px',
                      fontSize: '0.78rem',
                      color: '#64748b',
                      lineHeight: 1.5
                    }}>
                      Pick {videoOptions.limits?.minPhotos ?? 5}–{videoOptions.limits?.maxPhotos ?? 15} photos. Drag the strip below to reorder how they play in the video.
                    </p>

                    {/* Ordered strip — only visible once at least one photo
                        is picked. Each tile is draggable for reordering. */}
                    {selectedPhotos.length > 0 && (
                      <div style={{
                        display: 'flex',
                        gap: '8px',
                        overflowX: 'auto',
                        padding: '8px 4px 12px',
                        marginBottom: '14px',
                        background: '#ffffff',
                        border: '1px solid rgba(15, 23, 42, 0.08)',
                        borderRadius: '10px'
                      }}>
                        {selectedPhotos.map((url, idx) => (
                          <div
                            key={url + idx}
                            draggable
                            onDragStart={() => handlePhotoDragStart(idx)}
                            onDragOver={(e) => handlePhotoDragOver(e, idx)}
                            onDrop={() => handlePhotoDrop(idx)}
                            onDragEnd={handlePhotoDragEnd}
                            style={{
                              position: 'relative',
                              flex: '0 0 auto',
                              width: '72px',
                              height: '72px',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              cursor: 'grab',
                              border: dragOverIndex === idx
                                ? '2px solid #1e3a8a'
                                : '2px solid transparent',
                              opacity: dragIndex === idx ? 0.4 : 1,
                              transition: 'border-color 0.15s, opacity 0.15s'
                            }}
                          >
                            <img
                              src={url}
                              alt={`Selected ${idx + 1}`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                pointerEvents: 'none'
                              }}
                            />
                            <div style={{
                              position: 'absolute',
                              top: '3px',
                              left: '3px',
                              width: '20px',
                              height: '20px',
                              borderRadius: '50%',
                              background: '#1e3a8a',
                              color: '#ffffff',
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                            }}>
                              {idx + 1}
                            </div>
                            <button
                              onClick={() => togglePhotoSelection(url)}
                              aria-label="Remove photo"
                              style={{
                                position: 'absolute',
                                top: '3px',
                                right: '3px',
                                width: '18px',
                                height: '18px',
                                borderRadius: '50%',
                                background: 'rgba(15, 23, 42, 0.75)',
                                color: '#ffffff',
                                border: 'none',
                                fontSize: '0.7rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 0,
                                lineHeight: 1
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Selectable grid of all property photos. Clicking a
                        tile toggles selection; selected tiles get a sage
                        ring and order badge. Disabled state kicks in once
                        the max cap is hit so further taps no-op visibly. */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
                      gap: '8px'
                    }}>
                      {getPropertyPhotoUrls(videoModalProperty).map((url, idx) => {
                        const selectedIdx = selectedPhotos.indexOf(url);
                        const isSelected = selectedIdx !== -1;
                        const max = videoOptions.limits?.maxPhotos ?? 15;
                        const atCap = !isSelected && selectedPhotos.length >= max;
                        return (
                          <button
                            key={url + idx}
                            onClick={() => togglePhotoSelection(url)}
                            disabled={atCap}
                            style={{
                              position: 'relative',
                              aspectRatio: '1 / 1',
                              padding: 0,
                              border: isSelected
                                ? '2px solid #1e3a8a'
                                : '2px solid transparent',
                              borderRadius: '10px',
                              overflow: 'hidden',
                              cursor: atCap ? 'not-allowed' : 'pointer',
                              background: '#e2e8f0',
                              opacity: atCap ? 0.4 : 1,
                              transition: 'border-color 0.15s, transform 0.15s'
                            }}
                          >
                            <img
                              src={url}
                              alt={`Property photo ${idx + 1}`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                            {isSelected && (
                              <div style={{
                                position: 'absolute',
                                top: '6px',
                                left: '6px',
                                minWidth: '22px',
                                height: '22px',
                                padding: '0 6px',
                                borderRadius: '11px',
                                background: '#1e3a8a',
                                color: '#ffffff',
                                fontSize: '0.72rem',
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.25)'
                              }}>
                                {selectedIdx + 1}
                              </div>
                            )}
                          </button>
                        );
                      })}
                      {getPropertyPhotoUrls(videoModalProperty).length === 0 && (
                        <div style={{
                          gridColumn: '1 / -1',
                          padding: '24px',
                          textAlign: 'center',
                          color: '#94a3b8',
                          fontSize: '0.85rem',
                          background: '#ffffff',
                          border: '1px dashed rgba(15, 23, 42, 0.12)',
                          borderRadius: '10px'
                        }}>
                          This listing has no photos to choose from yet.
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ────────── RIGHT COLUMN: Options + cost ────────── */}
                  <div style={{
                    flex: '1 1 360px',
                    minWidth: '300px',
                    padding: '24px 28px 24px 24px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                    background: '#ffffff'
                  }}>
                    {/* Format presets — 16:9 is always on (included in base
                        price, can't be toggled off). Square (1:1) and
                        Vertical (9:16) are optional add-ons. We render
                        each as a card-like button with the aspect-ratio
                        icon, format name, and a subtitle describing what
                        it's for. The locked 16:9 card has a "Included"
                        badge instead of a checkmark and is non-clickable. */}
                    <div>
                      <h3 style={{
                        margin: '0 0 10px',
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        color: '#0f172a'
                      }}>
                        Output formats
                      </h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {(() => {
                          const FORMAT_META = {
                            '16:9': {
                              label: 'Landscape',
                              subtitle: 'YouTube, websites, presentations',
                              dims: { width: 22, height: 14 },
                            },
                            '1:1': {
                              label: 'Square',
                              subtitle: 'Instagram feed posts, Facebook',
                              dims: { width: 18, height: 18 },
                            },
                            '9:16': {
                              label: 'Vertical',
                              subtitle: 'Reels, TikTok, Stories, Shorts',
                              dims: { width: 14, height: 22 },
                            },
                          };
                          // Render in a consistent order regardless of API
                          // ordering: Landscape (locked) first, then square,
                          // then vertical.
                          const presetOrder = ['16:9', '1:1', '9:16'];
                          const available = videoOptions.selectors?.formatPresets || [];
                          return presetOrder
                            .filter((p) => available.includes(p))
                            .map((preset) => {
                              const meta = FORMAT_META[preset] || {
                                label: preset, subtitle: '', dims: { width: 16, height: 16 },
                              };
                              const on = selectedFormats.has(preset);
                              const locked = preset === '16:9';
                              return (
                                <button
                                  key={preset}
                                  onClick={() => toggleFormat(preset)}
                                  disabled={locked}
                                  style={{
                                    padding: '12px 14px',
                                    borderRadius: '10px',
                                    fontSize: '0.82rem',
                                    fontWeight: 500,
                                    cursor: locked ? 'default' : 'pointer',
                                    background: on ? '#1e3a8a' : '#ffffff',
                                    color: on ? '#ffffff' : '#0f172a',
                                    border: on
                                      ? '1px solid #1e3a8a'
                                      : '1px solid rgba(15, 23, 42, 0.15)',
                                    transition: 'all 0.15s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    textAlign: 'left',
                                    width: '100%',
                                  }}
                                >
                                  {/* Aspect-ratio icon */}
                                  <span style={{
                                    display: 'inline-block',
                                    width: `${meta.dims.width}px`,
                                    height: `${meta.dims.height}px`,
                                    borderRadius: '2px',
                                    border: on
                                      ? '1.5px solid #ffffff'
                                      : '1.5px solid #64748b',
                                    background: 'transparent',
                                    flexShrink: 0,
                                  }} />
                                  {/* Label + subtitle */}
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{
                                      fontSize: '0.86rem',
                                      fontWeight: 600,
                                      color: on ? '#ffffff' : '#0f172a',
                                    }}>
                                      {meta.label} · {preset}
                                    </div>
                                    <div style={{
                                      fontSize: '0.72rem',
                                      marginTop: '1px',
                                      color: on ? 'rgba(255,255,255,0.78)' : '#64748b',
                                    }}>
                                      {meta.subtitle}
                                    </div>
                                  </div>
                                  {/* Status badge — "Included" for locked,
                                      "+10" for add-ons, "Added" for selected. */}
                                  <span style={{
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    padding: '3px 8px',
                                    borderRadius: '999px',
                                    background: locked
                                      ? (on ? 'rgba(255,255,255,0.18)' : '#f1f5f9')
                                      : on
                                        ? 'rgba(255,255,255,0.18)'
                                        : '#f1f5f9',
                                    color: locked
                                      ? (on ? '#ffffff' : '#475569')
                                      : on
                                        ? '#ffffff'
                                        : '#475569',
                                    flexShrink: 0,
                                  }}>
                                    {locked
                                      ? 'Included'
                                      : on
                                        ? 'Added'
                                        : `+${videoOptions.pricing?.schedule?.formatAddons?.oneExtra ?? 10}`}
                                  </span>
                                </button>
                              );
                            });
                        })()}
                      </div>
                      {/* Tier hint: tell the agent both extras bundle */}
                      {(() => {
                        const has1x1 = selectedFormats.has('1:1');
                        const has9x16 = selectedFormats.has('9:16');
                        const oneExtra = videoOptions.pricing?.schedule?.formatAddons?.oneExtra ?? 10;
                        const bothExtras = videoOptions.pricing?.schedule?.formatAddons?.bothExtras ?? 15;
                        const savings = (oneExtra * 2) - bothExtras;
                        // Hint about the bundle when only one extra is selected
                        if (savings > 0 && ((has1x1 && !has9x16) || (!has1x1 && has9x16))) {
                          return (
                            <p style={{
                              margin: '8px 2px 0',
                              fontSize: '0.72rem',
                              color: '#64748b',
                            }}>
                              Add both extra formats for {bothExtras} pts (save {savings})
                            </p>
                          );
                        }
                        return null;
                      })()}
                    </div>

                    {/* Motion style is seeded from selectors.defaultMotionStyle
                        in state and submitted with the job, but we no longer
                        expose it as a user-facing selector — keeps the UI
                        focused on the choices that matter for the cost
                        breakdown. */}

                    {/* Music */}
                    <div>
                      <h3 style={{
                        margin: '0 0 8px',
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        color: '#0f172a'
                      }}>
                        Music track
                      </h3>
                      <select
                        value={musicTrack}
                        onChange={(e) => setMusicTrack(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          border: '1px solid rgba(15, 23, 42, 0.15)',
                          background: '#ffffff',
                          fontSize: '0.88rem',
                          color: '#0f172a',
                          cursor: 'pointer'
                        }}
                      >
                        {(videoOptions.musicTracks || []).map(t => (
                          <option key={t._id} value={t._id}>
                            {t.title}{t.artist ? ` — ${t.artist}` : ''}{t.mood ? ` (${t.mood})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Overlay add-ons. Toggle ids and pricing schedule
                        keys both match the backend (videoConfig.js), so
                        live cost preview and submit payload use the same
                        identifiers end-to-end. Each line item gets a
                        +N pts badge when the schedule prices it. */}
                    <div>
                      <h3 style={{
                        margin: '0 0 10px',
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        color: '#0f172a'
                      }}>
                        Overlays
                      </h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {[
                          {
                            id: 'showListingData',
                            label: 'Listing details',
                            sublabel: 'Price, beds, baths, sqft overlaid on the video',
                            scheduleKey: 'listingDetails'
                          },
                          {
                            id: 'showAgentBranding',
                            label: 'Agent branding',
                            sublabel: 'Your name, photo, and contact info on the closing frame',
                            scheduleKey: 'agentBranding'
                          }
                        ].map(addOn => {
                          const cost = videoOptions.pricing?.schedule?.[addOn.scheduleKey] ?? 0;
                          return (
                            <label
                              key={addOn.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '10px',
                                padding: '10px 12px',
                                background: '#fafaf8',
                                border: '1px solid rgba(15, 23, 42, 0.08)',
                                borderRadius: '8px',
                                cursor: 'pointer'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                                <input
                                  type="checkbox"
                                  checked={!!overlayToggles[addOn.id]}
                                  onChange={(e) => setOverlayToggles(prev => ({
                                    ...prev,
                                    [addOn.id]: e.target.checked
                                  }))}
                                  style={{ accentColor: '#1e3a8a', width: 16, height: 16 }}
                                />
                                <div>
                                  <div style={{ fontSize: '0.85rem', color: '#0f172a' }}>
                                    {addOn.label}
                                  </div>
                                  <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 2 }}>
                                    {addOn.sublabel}
                                  </div>
                                </div>
                              </div>
                              {cost > 0 && (
                                <span style={{
                                  fontSize: '0.78rem',
                                  fontWeight: 600,
                                  color: '#1e3a8a',
                                  whiteSpace: 'nowrap'
                                }}>
                                  +{cost} pts
                                </span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* Cost breakdown — live, recomputed every render.
                        Sits right after the option selectors so the agent
                        sees price impact in context as they toggle. */}
                    <div style={{
                      padding: '14px 16px',
                      background: '#fafaf8',
                      border: '1px solid rgba(15, 23, 42, 0.08)',
                      borderRadius: '10px'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px'
                      }}>
                        <h3 style={{
                          margin: 0,
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          color: '#0f172a',
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em'
                        }}>
                          Cost breakdown
                        </h3>
                        <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                          Balance: <strong style={{ color: '#0f172a' }}>
                            {videoOptions.agent?.pointsBalance ?? 0} pts
                          </strong>
                        </span>
                      </div>
                      {(() => {
                        const { lineItems, total } = computeCostBreakdown();
                        const balance = videoOptions.agent?.pointsBalance ?? 0;
                        const insufficient = balance < total;
                        return (
                          <>
                            {lineItems.map((line, i) => (
                              <div key={i} style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontSize: '0.82rem',
                                color: '#475569',
                                padding: '3px 0'
                              }}>
                                <span>{line.label}</span>
                                <span>{line.cost} pts</span>
                              </div>
                            ))}
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              fontSize: '0.95rem',
                              fontWeight: 700,
                              color: insufficient ? '#b91c1c' : '#0f172a',
                              borderTop: '1px solid rgba(15, 23, 42, 0.1)',
                              marginTop: '8px',
                              paddingTop: '8px'
                            }}>
                              <span>Total</span>
                              <span>{total} pts</span>
                            </div>
                            {insufficient && (
                              <div style={{
                                marginTop: '8px',
                                fontSize: '0.75rem',
                                color: '#b91c1c'
                              }}>
                                You need {total - balance} more points to generate this video.
                              </div>
                            )}
                            {videoOptions.limits?.monthlyUsed != null && videoOptions.limits?.monthlyCap != null && (
                              <div style={{
                                marginTop: '8px',
                                fontSize: '0.72rem',
                                color: '#64748b'
                              }}>
                                {videoOptions.limits.monthlyUsed} of {videoOptions.limits.monthlyCap} videos used this month
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer — real submit handler now wired up. Shows selection
                summary on the left, Cancel + Generate on the right, and
                a submit-error banner above if the POST failed. */}
            {submitError && !submitted && (
              <div style={{
                padding: '10px 28px',
                background: '#fef2f2',
                borderTop: '1px solid #fecaca',
                color: '#991b1b',
                fontSize: '0.82rem',
                flexShrink: 0
              }}>
                {submitError}
              </div>
            )}
            <div style={{
              padding: '16px 28px',
              borderTop: '1px solid rgba(15, 23, 42, 0.08)',
              background: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: submitted ? 'flex-end' : 'space-between',
              gap: '12px',
              flexShrink: 0
            }}>
              {submitted ? (
                // Post-submit footer: just a single Done button. The
                // configuration summary and cost are no longer relevant
                // — those decisions are locked in. One clear action
                // (close the modal) and the agent moves on.
                <button
                  onClick={closeVideoModal}
                  className="btn btn-primary"
                  style={{
                    minWidth: '140px',
                    background: '#1e3a8a',
                    borderColor: '#1e3a8a',
                    color: '#ffffff'
                  }}
                >
                  Done
                </button>
              ) : (
                <>
                  <div style={{
                    fontSize: '0.8rem',
                    color: '#64748b'
                  }}>
                    {videoOptions ? (
                      <>
                        {selectedPhotos.length} photo{selectedPhotos.length === 1 ? '' : 's'} ·{' '}
                        {selectedFormats.size} format{selectedFormats.size === 1 ? '' : 's'}
                      </>
                    ) : (
                      'Loading options...'
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={closeVideoModal}
                      className="btn btn-secondary"
                      style={{ minWidth: '90px' }}
                      disabled={submitting}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmitVideoJob}
                      disabled={isSubmitDisabled()}
                      className="btn btn-primary"
                      style={{
                        minWidth: '140px',
                        background: isSubmitDisabled() ? 'rgba(30, 58, 138, 0.4)' : '#1e3a8a',
                        borderColor: isSubmitDisabled() ? 'rgba(30, 58, 138, 0.4)' : '#1e3a8a',
                        color: '#ffffff',
                        cursor: isSubmitDisabled() ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {submitting ? 'Starting...' : (
                        videoOptions
                          ? `Generate · ${computeCostBreakdown().total} pts`
                          : 'Generate'
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AgentPropertyList;